/** @jsx jsx */

import { clamp } from 'lodash';

import { Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

const SIZES_MAP = {
  small: [120, 8, 11],
  medium: [210, 10, 13],
  large: [300, 12, 15],
};

const meterCss = css({
  position: 'relative',
});

const svgCss = css({
  display: 'block',
  overflow: 'visible',
  transform: 'rotate(180deg)',
});

const calcDashOffset = (radius: number, value: number) => {
  // Based on a half a circle
  const scaled = (value / 100) * 0.5;
  const circ = Math.PI * 2 * radius;
  return circ * (1 - scaled);
};

const arcCss = css({
  fill: 'none',
  strokeLinecap: 'round',
  transition:
    'stroke-dashoffset 2s cubic-bezier(0.19, 1, 0.22, 1), stroke 2s ease-out',
});

const needleBaseCss = css({
  transform: 'rotate(180deg)',
  transformOrigin: 'center',
});

const needleCss = css({
  transformOrigin: '50% 12%',
  willChange: 'transform',
  transition: 'transform 2s cubic-bezier(0.19, 1, 0.22, 1)',
});

type NeedleProps = {
  value: number;
  width: number;
};

const Needle = (props: NeedleProps) => {
  // Based on 190deg to give an extra 5deg bend on either side since the
  // needle sits a few pixels above the absolute bottom of the arc.
  const needleStyle = {
    transform: `rotate(${(props.value / 100) * 190 - 95}deg)`,
  };
  return (
    <svg height={props.width / 2} viewBox="0 0 14 63" width={props.width}>
      <g css={needleCss} style={needleStyle}>
        <g
          css={needleBaseCss}
          fill="none"
          fillRule="evenodd"
          stroke="none"
          strokeWidth="1"
        >
          <g transform="translate(-56, -4)">
            <path
              d="M62.2923611,4.8516715 L59.9981902,62.0473693 C59.9864487,62.3414667 60.3066188,62.5846828 60.7055428,62.5846828 L65.2941711,62.5846828 C65.6930951,62.5846828 66.0135516,62.3414667 66.0018101,62.0473693 L63.7073528,4.8516715 C63.6804333,4.17649334 62.319567,4.17649334 62.2923611,4.8516715"
              fill="#354147"
            />
            <path
              d="M68.7003785,59.5405777 C68.7003785,62.6888757 66.1482979,65.2409562 63,65.2409562 C59.8517021,65.2409562 57.2996215,62.6888757 57.2996215,59.5405777 C57.2996215,56.3922798 59.8517021,53.8401992 63,53.8401992 C66.1482979,53.8401992 68.7003785,56.3922798 68.7003785,59.5405777"
              fill="#F1F4F5"
            />
            <path
              d="M68.7003785,59.5405777 L69.8443785,59.5405777 C69.8443785,63.3206894 66.7801117,66.3849563 63,66.3849563 C59.2198883,66.3849563 56.1556215,63.3206894 56.1556215,59.5405777 C56.1556215,55.760466 59.2198883,52.6961992 63,52.6961992 C66.7801117,52.6961992 69.8443785,55.760466 69.8443785,59.5405777 L68.7003785,59.5405777 Z M68.7003785,59.5405777 L67.5563785,59.5405777 C67.5563785,57.0240935 65.5164842,54.9841992 63,54.9841992 C60.4835158,54.9841992 58.4436215,57.0240935 58.4436215,59.5405777 C58.4436215,62.0570619 60.4835158,64.0969562 63,64.0969562 C65.5164842,64.0969562 67.5563785,62.0570619 67.5563785,59.5405777 L68.7003785,59.5405777 Z"
              fill="#5F6C72"
              fillRule="nonzero"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

type MeterProps = {
  /** (Optional) Color of the value arc. */
  barColor?: string;
  /** Color of the base background arc. */
  meterColor: string;
  /** Value of the Meter (0 - 100). */
  percent: number;
  /** Size of the meter. */
  size: keyof typeof SIZES_MAP;
};

/**
 * Displays a neat 180º meter with a needle for progress.
 */
const Meter = (props: MeterProps) => {
  const [width, barWidth, valueBarWidth] = SIZES_MAP[props.size];
  const center = width / 2;
  const radius = center - barWidth;
  const clampedValue = clamp(props.percent, 0, 100);
  const barOffset = barWidth / 2;
  const arcStyle = {
    strokeDasharray: Math.PI * 2 * radius,
  };
  return (
    <div css={meterCss}>
      <svg
        css={svgCss}
        height={center}
        viewBox={`0 0 ${width} ${center}`}
        width={width}
      >
        <circle
          css={arcCss}
          cx={center}
          cy={barOffset}
          r={radius}
          stroke={props.meterColor}
          strokeDashoffset={calcDashOffset(radius, 100)}
          strokeWidth={barWidth}
          style={arcStyle}
        />
        <circle
          css={arcCss}
          cx={center}
          cy={barOffset}
          r={radius}
          stroke={props.barColor}
          strokeDashoffset={calcDashOffset(radius, clampedValue)}
          strokeWidth={valueBarWidth}
          style={arcStyle}
        />
        <Needle value={clampedValue} width={width} />
      </svg>
    </div>
  );
};

Meter.defaultProps = {
  meterColor: Colors.adamantium,
  size: 'small',
};

export default Meter;
